import { lazy, Suspense, useEffect, useState } from 'react'
import { haalIk, opUitgelogd } from './lib/api'
import { navigeer, useRoute } from './lib/router'
import { lees, meld, useStaat, zet } from './lib/staat'
import Poort from './onderdelen/Poort'
import Shell from './onderdelen/Shell'
import { Laden } from './onderdelen/ui'
import Overzicht from './onderdelen/Overzicht'
import MijnWerk from './onderdelen/MijnWerk'
import Projecten from './onderdelen/Projecten'
import ProjectScherm from './onderdelen/ProjectScherm'
import TaakRoute from './onderdelen/TaakRoute'

/* De zware schermen pas laden als iemand ze opent. Het whiteboard en de
   GitHub-schermen samen zijn groter dan de rest van de app. */
const Whiteboard = lazy(() => import('./onderdelen/whiteboard/Whiteboard'))
const Whiteboards = lazy(() => import('./onderdelen/whiteboard/Whiteboards'))
const Repos = lazy(() => import('./onderdelen/code/Repos'))
const RepoScherm = lazy(() => import('./onderdelen/code/RepoScherm'))
const Changelog = lazy(() => import('./onderdelen/code/Changelog'))
const Assistent = lazy(() => import('./onderdelen/ai/Assistent'))

type Stand = 'kijken' | 'binnen' | 'buiten'

export default function App() {
  const [stand, setStand] = useState<Stand>('kijken')
  const ik = useStaat((s) => s.ik)

  /* Een fout uit de adresbalk (?fout=geen-toegang) wordt één keer getoond en
     dan weggehaald, zodat hij er na verversen niet nog steeds staat. */
  const [fout] = useState(() => new URLSearchParams(location.search).get('fout'))
  useEffect(() => {
    if (!fout) return
    const u = new URL(location.href)
    u.searchParams.delete('fout')
    history.replaceState(null, '', u.pathname + u.search)
  }, [fout])

  useEffect(() => {
    opUitgelogd(() => { zet({ ik: null }); setStand('buiten') })
    void haalIk().then((s) => {
      if (!s) { setStand('buiten'); return }
      zet({ ik: s.ik, github: s.github })
      setStand('binnen')
    })
  }, [])

  if (stand === 'kijken') return <div className="opstart"><Laden /></div>
  if (stand === 'buiten' || !ik) return <Poort fout={fout} />

  return (
    <Shell>
      <Suspense fallback={<Laden />}>
        <Scherm />
      </Suspense>
    </Shell>
  )
}

function Scherm() {
  const { delen } = useRoute()
  const [eerste, tweede, derde] = delen

  switch (eerste) {
    case undefined: return <Overzicht />
    case 'mijn': return <MijnWerk />
    case 'projecten': return <Projecten />
    case 'p': return tweede ? <ProjectScherm id={tweede} tab={derde ?? 'bord'} /> : <Projecten />
    case 'taak': return tweede ? <TaakRoute id={tweede} /> : <Overzicht />
    case 'whiteboards': return <Whiteboards />
    case 'wb': return tweede ? <Whiteboard id={tweede} /> : <Whiteboards />
    case 'code': return tweede ? <RepoScherm repo={tweede} tab={derde ?? 'overzicht'} rest={delen.slice(3)} /> : <Repos />
    case 'changelog': return <Changelog />
    case 'ai': return <Assistent />
    default: return <NietGevonden />
  }
}

function NietGevonden() {
  useEffect(() => {
    /* Een oud of verkeerd adres: terug naar het begin, maar wel met een
       melding, anders lijkt het of de link werkte. */
    if (lees().ik) {
      meld('Dat adres bestaat hier niet (meer). Je bent terug op het overzicht.', 'info')
      navigeer('/', true)
    }
  }, [])
  return null
}
